import { isGregorian, monthName, type TimeUnit } from '@dispositio/core';
import { useLayoutEffect, useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { parseDateTime, parseTime, toDisplay } from '../../lib/dateTime';

interface Props {
  /** `JJJJ-MM-TTThh:mm`, ein Wert einer eigenen Zählung („Tag 12, 06:00“) oder leer */
  value: string;
  /** `false`: abgelehnt, das Feld zeigt wieder den gespeicherten Wert */
  onCommit: (iso: string) => boolean | void;
  /**
   * Auch eine eigene Zählung annehmen: `any` mit Einheitswort („Tag 12, 06:00“); eine Einheit (Zeitstrahl mit eigener Zählung):
   * auch eine bloße Zahl, ohne Kalender. Fehlt sie, nur Datum und Uhrzeit.
   */
  unit?: TimeUnit | 'any';
  id?: string;
  ariaLabel?: string;
  testId?: string;
}

/**
 * Datum und Uhrzeit im 24-Stunden-Format: Eingabe als „TT.MM.JJJJ hh:mm“, übernommen beim Verlassen des Feldes oder mit Enter.
 * Der Kalenderknopf wählt das Datum, die Uhrzeit bleibt. Ebenso geht ein Wert einer eigenen Zählung („Tag 12, 06:00“).
 */
export function DateTimeInput({ value, onCommit, unit, id, ariaLabel, testId }: Props) {
  const [draft, setDraft] = useState(toDisplay(value));
  const [error, setError] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const editing = useRef(false);
  // neuer gespeicherter Wert: übernehmen – aber nicht, während man gerade im Feld schreibt. Im selben Schritt wie die Änderung
  // (Layout-Effekt): sonst kann der neue Text erst nach dem Fokussieren und Markieren im Feld landen, die Markierung geht
  // verloren und die Eingabe hängt sich an den alten Text an.
  useLayoutEffect(() => {
    if (editing.current) return;
    setDraft(toDisplay(value));
    setError(false);
  }, [value]);

  // eigener Kalender (PF 16): Monatsnamen statt Zahlen, kein Datumswähler des Browsers (der kennt nur den gregorianischen)
  const own = !isGregorian();
  const example = `12. ${monthName(1)} 312 08:00`;
  const commit = (text: string): void => {
    const iso = unit ? parseTime(text, unit === 'any' ? undefined : unit) : parseDateTime(text);
    if (iso === null) return setError(true);
    setError(false);
    if (iso === value) return setDraft(toDisplay(iso));
    setDraft(toDisplay(iso));
    if (onCommit(iso) === false) setDraft(toDisplay(value));
  };

  return (
    <div className="col">
      <div className="row datetime">
        <input
          id={id}
          type="text"
          inputMode={own ? 'text' : 'numeric'}
          value={draft}
          placeholder={unit && unit !== 'any' ? de.common.countPlaceholder[unit] : own ? de.common.calendarPlaceholder(monthName(1)) : de.common.dateTimePlaceholder}
          aria-label={ariaLabel}
          aria-invalid={error}
          onFocus={() => {
            editing.current = true;
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => {
            editing.current = false;
            commit(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(e.currentTarget.value);
            }
          }}
          data-testid={testId}
        />
        {(!unit || unit === 'any') && !own && (
          <button
            type="button"
            className="btn ghost icon"
            aria-label={de.common.pickDate}
            title={de.common.pickDate}
            onClick={() => {
              const el = picker.current;
              if (!el) return;
              el.value = value.slice(0, 10);
              try {
                el.showPicker();
              } catch {
                el.click();
              }
            }}
          >
            📅
          </button>
        )}
        <input
          ref={picker}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          className="datetime-picker"
          onChange={(e) => {
            if (!e.target.value) return;
            const time = /T(\d{2}:\d{2})$/.exec(value)?.[1] ?? '00:00';
            commit(`${e.target.value}T${time}`);
          }}
        />
      </div>
      {error && (
        <div className="error-text small" role="alert">
          {own && (!unit || unit === 'any') ? de.common.calendarInvalid(example) : unit === 'any' ? de.common.timeInvalid : unit ? de.common.countInvalid(de.common.countPlaceholder[unit]) : de.common.dateTimeInvalid}
        </div>
      )}
    </div>
  );
}
